import { createContext, useContext, useEffect, useState } from "react";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    isAuthenticated: false,
    user: null,
    role: null, // 'user', 'purohit', or 'temple'
    token: null,
  });
  const [loading, setLoading] = useState(true);
  const [purohitProfile, setPurohitProfile] = useState(null);
  const [templeProfile,setTempleProfile]=useState(null);

  // Initialize auth from localStorage on mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedAuth = localStorage.getItem("auth");

        if (storedAuth) {
          const parsedAuth = JSON.parse(storedAuth);

          // Validate stored data
          if (parsedAuth.token && parsedAuth.user && parsedAuth.role) {
            setAuth(parsedAuth);

            // Load purohit profile if role is purohit
            if (parsedAuth.role === "purohit" && parsedAuth.user.profile) {
              setPurohitProfile(parsedAuth.user.profile);
            }
            if (parsedAuth.role === "temple" && parsedAuth.user.profile) {
  setTempleProfile(parsedAuth.user.profile);
}
          } else {
            // Clear invalid data
            localStorage.removeItem("auth");
          }
        }
      } catch (error) {
        console.error("Error loading auth from localStorage:", error);
        localStorage.removeItem("auth");
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

 

  useEffect(() => {
  if (auth.isAuthenticated && auth.token) {
    const minimalAuth = {
      isAuthenticated: true,
      token: auth.token,
      role: auth.role,
      user: {
        id: auth.user?.id,
        name: auth.user?.name,
        email: auth.user?.email,
      },
    };

    localStorage.setItem("auth", JSON.stringify(minimalAuth));
  } else {
    localStorage.removeItem("auth");
  }
}, [auth.isAuthenticated, auth.token, auth.role, auth.user?.id]);


  const login = async (authData) => {
    try {
      // authData should contain: { isAuthenticated, user, role, token }
      setAuth(authData);

      // Set purohit profile if applicable
      if (authData.role === "purohit" && authData.user?.profile) {
        setPurohitProfile(authData.user.profile);
      }
      if(authData.role === "temple" && authData.user?.profile){
        setTempleProfile(authData.user.profile)
      }

      return { role: authData.role, user: authData.user };
    } catch (error) {
      console.error("Login error in AuthContext:", error);
      throw error;
    }
  };

  const logout = () => {
    setAuth({
      isAuthenticated: false,
      user: null,
      role: null,
      token: null,
    });
    setPurohitProfile(null);
    setTempleProfile(null);
    localStorage.removeItem("auth");
    localStorage.removeItem("currentUserId");
  };

  const updateUser = (userData) => {
    setAuth((prev) => ({
      ...prev,
      user: { ...prev.user, ...userData },
    }));
  };

  const getCurrentUser = () => {
    if (!auth.isAuthenticated || !auth.user) return null;
    return auth.user;
  };

  const hasPurohitProfile = () => {
    // Check both existence AND completion status
    if (auth.role !== "purohit" || !auth.user) {
      return false;
    }

    // Check if profile is complete - could be in multiple locations
    return (
      auth.user.isComplete === true || 
      auth.user.profile?.isComplete === true ||
      purohitProfile?.isComplete === true
    );
  };
  

  const hasTempleProfile = () => {
  if (auth.role !== "temple" || !auth.user) {
    return false;
  }

  return (
    auth.user.isComplete === true ||
    auth.user.profile?.isComplete === true ||
    templeProfile?.isComplete === true
  );
};

  const updatePurohitProfile = (profileData) => {
    console.log("Updating purohit profile:", profileData);

    setPurohitProfile(profileData);

    setAuth((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        ...profileData, // Merge all profile data including isComplete
        profile: profileData,
      },
    }));
  };

  const updateTempleProfile = (profileData) => {
  console.log("Updating temple profile:", profileData);

  setTempleProfile(profileData);

  setAuth((prev) => ({
    ...prev,
    user: {
      ...prev.user,
      ...profileData,
      profile: profileData,
    },
  }));
};


  return (
    <AuthContext.Provider
      value={{
        auth,
        login,
        logout,
        updateUser,
        updatePurohitProfile,
        purohitProfile,
        getCurrentUser,
        hasPurohitProfile,
        updateTempleProfile,
        templeProfile,
        hasTempleProfile,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};